import { useCallback, useContext, useState } from "react";
import { MenuItem, FormGroup, AnchorButton } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, MultiSelect } from "@blueprintjs/select";
import { useReference, ConfigurationItem } from "src/hooks/useReference";
import { CustomFormikReferenceComponentProps } from "../form/CustomFormikComponents";
import { SnContext } from "src/utils/context";

const ListCollectorSelect = MultiSelect.ofType<ConfigurationItem>();
const filterItems: ItemPredicate<ConfigurationItem> = (query, item) => {
    return item.name.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};
const areRefsEqual = (refA: ConfigurationItem, refB: ConfigurationItem) => refA.sysId === refB.sysId;
const renderTag = (item: ConfigurationItem) => item.name;

export default function UserInputListCollector({
    companyId,
    tableRefName,
    label,
    required,
    name,
    setFieldValue
}: CustomFormikReferenceComponentProps) {
    const [data, loading, error] = useReference(companyId, tableRefName);
    const [selected, setSelected] = useState<ConfigurationItem[]>([]);
    const snContext = useContext(SnContext);
    const snUri = `https://${snContext}.service-now.com`;
    //todo: figure out why clear button is triggered by individual tag clear
    // const handleClear = () => setSelected([]);
    // const clearButton = selected.length > 0 ? <Button icon="cross" minimal={true} onClick={handleClear} /> : undefined;

    const isItemSelected = useCallback(
        (item: ConfigurationItem) => {
            return selected.indexOf(item) !== -1;
        },
        [selected]
    );

    // formik object needs to be update separate from the selected state
    // because of how ci names and sysids are stored on the backend.
    //todo: change the render model to use formik state so we're not storing duplicate state
    const updateFormikValues = useCallback(
        (updated: ConfigurationItem[]) => {
            setFieldValue(
                `${name}.name`,
                updated.map(i => i.name)
            );
            setFieldValue(
                `${name}.value`,
                updated.map(i => i.sysId)
            );
        },
        [name, setFieldValue]
    );
    const handleSelect = useCallback(
        item => {
            const updated = selected.includes(item)
                ? selected.filter(i => i.sysId !== item.sysId)
                : [...selected, item];
            setSelected(updated);
            updateFormikValues(updated);
        },
        [selected, updateFormikValues]
    );
    const handleRemove = useCallback(
        (item: ConfigurationItem) => {
            const updated = selected.filter(i => i.sysId !== item.sysId);
            setSelected(updated);
            updateFormikValues(updated);
        },
        [selected, updateFormikValues]
    );

    const renderList: ItemRenderer<ConfigurationItem> = useCallback(
        (item, { modifiers, handleClick }) => {
            if (!modifiers.matchesPredicate) {
                return null;
            }
            return (
                <MenuItem
                    active={modifiers.active}
                    icon={isItemSelected(item) ? "tick" : "blank"}
                    key={item.sysId}
                    onClick={handleClick}
                    text={item.name}
                    shouldDismissPopover={false}
                    //!todo fix <a> descendant
                    labelElement={
                        <AnchorButton
                            intent="primary"
                            minimal
                            icon="link"
                            target="_blank"
                            href={`${snUri}/nav_to.do?uri=${tableRefName}.do?sys_id=${item.sysId}`}
                            // prevent click on parent
                            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => e.stopPropagation()}
                        />
                    }
                />
            );
        },
        [isItemSelected, snUri, tableRefName]
    );
    return (
        <FormGroup
            inline={false}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
            disabled={!!error || loading}
        >
            <ListCollectorSelect
                fill
                itemRenderer={renderList}
                itemsEqual={areRefsEqual}
                items={data ? data : []}
                noResults={<MenuItem disabled={true} text="No results." />}
                selectedItems={selected}
                onItemSelect={handleSelect}
                onRemove={handleRemove}
                popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                tagRenderer={renderTag}
                itemPredicate={filterItems}
                // tagInputProps={{
                //     onRemove: handleClear,
                //     rightElement: clearButton
                // }}
            />
        </FormGroup>
    );
}

// const refEqualsQuery = (ref: ConfigurationItem, query: string) => ref.name.toLowerCase() === query.toLowerCase();
// const arrayContainsRef = (refArr: Array<ConfigurationItem>, toFind: ConfigurationItem) =>
//     refArr.some(r => toFind.sysId === r.sysId);
